import { useMemo } from "react";
import { useFilters } from "@/context/FiltersContext";
import useHome from "@/lib/useHome";
import useIsMobile from "@/lib/useIsMobile";
import { FollowableName } from "@/components/FollowableName";
import usePersistentState from "@/lib/usePersistentState";
import { EVENT_CATEGORIES, canonRodeoEvent, money, fmtDate, dollarsPerMile } from "@/lib/ui";
import { DollarSign, MapPin, Navigation } from "lucide-react";

const ASSOC_TABS = [
  { k: "both", label: "Both" },
  { k: "CPRA", label: "CPRA" },
  { k: "PRCA", label: "PRCA" },
];

// The event's added money for a rodeo, from the parsed per-event purses (null if none).
function eventAmount(r, ev) {
  const p = (r.purses || []).find((x) => canonRodeoEvent(x.event) === canonRodeoEvent(ev));
  return p ? Number(p.amount) || 0 : null;
}
function startISO(r) {
  if (r.start_date) return r.start_date;
  const ds = (r.performances || []).map((p) => p.date).filter(Boolean).sort();
  return ds[0] || null;
}
const assocOf = (r) => ((r.associations || []).includes("CPRA") ? "CPRA" : (r.associations || []).includes("PRCA") ? "PRCA" : null);

export default function Payouts() {
  const m = useIsMobile();
  const { rodeos } = useFilters();
  const home = useHome();
  const [event, setEvent] = usePersistentState("rp_payouts_event", "Team Roping (Header)");
  const [assoc, setAssoc] = usePersistentState("rp_payouts_assoc", "both");

  const rows = useMemo(() => {
    const evSet = new Set([event]);
    return (rodeos || [])
      .map((r) => ({ r, amount: eventAmount(r, event), a: assocOf(r) }))
      .filter((x) => x.amount != null && x.amount > 0 && x.a)
      .filter((x) => assoc === "both" || x.a === assoc)
      .map((x) => ({ ...x, dpm: dollarsPerMile(x.r, home, evSet) }))
      .sort((p, q) => q.amount - p.amount);
  }, [rodeos, event, assoc, home]);

  // Theme (match Stock.js: dark on mobile, light on desktop)
  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "min-h-screen bg-[#F4F6F8]";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const card = m ? "bg-white/[0.03] border-white/10" : "bg-white border-[#E4E7EB]";
  const sub = m ? "text-slate-400" : "text-[#6B7280]";
  const chipOff = m ? "bg-white/5 border-white/10 text-slate-300" : "bg-white border-[#D7DBE0] text-[#44505F]";

  return (
    <div className={page} data-testid="payouts-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB] bg-white"}`}>
        <DollarSign className="w-6 h-6 text-[#16A34A]" />
        <div>
          <h1 className={`font-display text-2xl font-extrabold tracking-tight leading-none ${title}`}>Top Money by Event</h1>
          <p className={`text-xs mt-1 ${sub}`}>Richest rodeos for your event, ranked by added money</p>
        </div>
      </div>

      <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4">
        {/* Event chips */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1" data-testid="payouts-event-chips">
          {EVENT_CATEGORIES.map((e) => {
            const on = e === event;
            return (
              <button key={e} onClick={() => setEvent(e)} data-testid={`payouts-event-${e.replace(/[\s()]+/g, "-")}`}
                className={`shrink-0 px-3.5 py-2 rounded-full text-sm font-semibold border transition-colors ${on ? "bg-[#C8000F] border-[#C8000F] text-white" : chipOff}`}>
                {e}
              </button>
            );
          })}
        </div>

        {/* Assoc toggle */}
        <div className={`inline-flex rounded-lg overflow-hidden border ${m ? "border-white/10" : "border-[#D7DBE0]"}`} data-testid="payouts-assoc-toggle">
          {ASSOC_TABS.map((t) => {
            const on = t.k === assoc;
            return (
              <button key={t.k} onClick={() => setAssoc(t.k)} data-testid={`payouts-assoc-${t.k}`}
                className={`px-4 py-2 text-sm font-semibold transition-colors ${on ? "bg-[#1F6FEB] text-white" : m ? "bg-white/5 text-slate-300" : "bg-white text-[#44505F]"}`}>
                {t.label}
              </button>
            );
          })}
        </div>

        <div className={`text-sm ${sub}`} data-testid="payouts-count">
          <span className={`font-bold ${title}`}>{rows.length}</span> rodeo{rows.length === 1 ? "" : "s"} paying added money for <span className={`font-semibold ${title}`}>{event}</span>
        </div>

        {rows.length === 0 ? (
          <div className={`rounded-xl border px-5 py-10 text-center ${card} ${sub}`} data-testid="payouts-empty">
            No per-event added money on file for {event}{assoc !== "both" ? ` in ${assoc}` : ""} yet.
          </div>
        ) : (
          <div className="space-y-2" data-testid="payouts-list">
            {rows.map(({ r, amount, a, dpm }, i) => (
              <div key={r.id} data-testid={`payouts-row-${r.id}`}
                className={`flex items-center gap-3 rounded-xl border px-3.5 py-3 ${card}`}>
                <div className={`w-7 shrink-0 text-center font-extrabold font-display ${i < 3 ? "text-[#E6B35A]" : sub}`}>{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className={`font-display font-bold text-[15px] leading-tight truncate ${title}`}><FollowableName name={r.name} /></div>
                  <div className={`text-xs flex items-center gap-2 mt-0.5 ${sub}`}>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[r.city, r.state].filter(Boolean).join(", ") || "—"}</span>
                    {startISO(r) && <span>· {fmtDate(startISO(r))}</span>}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${a === "CPRA" ? "bg-[#C8000F]/15 text-[#F26D77]" : "bg-[#1F6FEB]/15 text-[#5B9CFF]"}`}>{a}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-extrabold text-[16px] text-[#16A34A] leading-none">{money(amount)}</div>
                  {dpm ? <div className="text-[11px] text-[#5CC46A] mt-1 inline-flex items-center gap-0.5"><Navigation className="w-3 h-3" />${dpm.toLocaleString()}/mi</div> : null}
                </div>
              </div>
            ))}
          </div>
        )}

        {!home && rows.some((x) => x.amount) && (
          <p className={`text-xs ${sub}`} data-testid="payouts-home-hint">Set your home base to see $/mi for the drive.</p>
        )}
      </div>
    </div>
  );
}
